import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, FilePlus2, ShieldCheck, ShieldOff, Gift, RotateCcw, Users, Crown, Clock, PoundSterling, FileText, Trash2, MessageSquare, Eye, Ban, UserPlus, PencilLine, MailPlus, BadgePoundSterling, Eraser, CalendarDays, KeyRound, Settings, UserCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import AccountSettings from "@/components/AccountSettings";
import RecipeSubmissions from "@/components/RecipeSubmissions";
import ReleaseNotesEditor from "@/components/ReleaseNotesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ACTIVITY, GOALS, TRAINING_SUPPORT, INTENSITY, SPORTS, DIET_PATTERNS } from "@/lib/planModel";
import { kgToLb, cmToFtIn } from "@/lib/units";

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-200 text-[10px] uppercase tracking-[0.2em]"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className="font-display text-3xl mt-2 tracking-tight text-white">{value}</p>
    </div>
  );
}

const STATUS_COLOR = {
  active: "text-[#34C759] border-[#34C759]/40", trialing: "text-[#4C9AFF] border-[#4C9AFF]/40",
  past_due: "text-[#FF9500] border-[#FF9500]/40", canceled: "text-[#FF3B30] border-[#FF3B30]/40",
  none: "text-zinc-200 border-white/20",
};

const labelOf = (arr, v) => (arr.find((x) => x.value === v)?.label) || v || "—";
const DAY_ABBR = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DRow({ k, v, warn }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-white/5">
      <span className="text-zinc-400 text-xs uppercase tracking-wider">{k}</span>
      <span className={`text-right text-sm ${warn ? "text-[#FF9500] font-semibold" : "text-white"}`}>{v}</span>
    </div>
  );
}

function PlanDetail({ p }) {
  const prof = p.profile || {};
  const wImperial = prof.weightUnit ? prof.weightUnit === "lb" : p.unitSystem === "imperial";
  const hImperial = prof.heightUnit ? prof.heightUnit === "ftin" : p.unitSystem === "imperial";
  const kg = parseFloat(prof.weight) || 0;
  const cm = parseFloat(prof.height) || 0;
  const lb = kg ? Math.round(kgToLb(kg)) : 0;
  const hi = cm ? cmToFtIn(cm) : { ft: 0, inch: 0 };
  const macros = p.macros || {};
  const diet = p.dietary || {};
  const days = (p.trainingWeek && p.trainingWeek.days) || [];
  const race = p.raceWeek || {};
  const t = (p.generated && p.generated.targets) || {};
  const lowWeight = kg > 0 && kg < 40;
  const lowEnergy = !!t.restEnergy && t.restEnergy < 1200;
  const exclusions = [...(diet.exclusions || []), ...(diet.customExclusions || [])];

  return (
    <div className="space-y-5 py-1" data-testid="admin-plan-detail-body">
      {(lowWeight || lowEnergy) && (
        <div data-testid="admin-detail-warning" className="border border-[#FF9500]/50 bg-[#FF9500]/10 p-3 text-sm text-[#FF9500]">
          {lowWeight ? (
            <>⚠ Body weight reads <b>{kg} kg ({lb} lb)</b>, entered in <b>{wImperial ? "lb" : "kg"}</b> — implausibly low; a value meant in kg may have been typed into the lb field.</>
          ) : (
            <>⚠ Rest-day energy is low (<b>{t.restEnergy} kcal</b>). Weight/units look fine (<b>{kg} kg</b>) — this is an aggressive Controlled Fat Loss deficit on a lighter athlete. A safety floor now applies; ask them to <b>regenerate</b>, or ease the goal to Maintenance.</>
          )}
        </div>
      )}

      <section>
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-2">Athlete Inputs</h3>
        <DRow k="Weight unit" v={wImperial ? "lb (imperial)" : "kg (metric)"} warn={wImperial} />
        <DRow k="Height unit" v={hImperial ? "ft-in (imperial)" : "cm (metric)"} />
        <DRow k="Body weight" v={`${kg} kg · ${lb} lb`} warn={lowWeight} />
        <DRow k="Height" v={`${cm} cm · ${hi.ft}'${hi.inch}"`} />
        <DRow k="Age" v={prof.age ? `${prof.age} yrs` : "—"} />
        <DRow k="Sex" v={prof.sex || "—"} />
        <DRow k="Baseline activity" v={labelOf(ACTIVITY, prof.activity)} />
        <DRow k="Training-energy support" v={labelOf(TRAINING_SUPPORT, prof.trainingSupport)} />
        {/* Hydration baseline is no longer athlete-editable — it only feeds
            in-session fluid targets and defaults to 1.5 L. */}
        <DRow k="Primary goal" v={labelOf(GOALS, p.goal)} />
      </section>

      <section>
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-2">Dietary</h3>
        <DRow k="Pattern" v={labelOf(DIET_PATTERNS, diet.pattern)} />
        <DRow k="Exclusions" v={exclusions.length ? exclusions.join(", ") : "None"} />
        <DRow k="Approved products" v={(diet.approvedProducts || []).length ? diet.approvedProducts.join(", ") : "None"} />
      </section>

      <section>
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-2">Macros</h3>
        <DRow k="Mode" v={macros.mode === "manual" ? "Manual (custom)" : "Auto"} warn={macros.mode === "manual"} />
        <DRow k="Protein" v={macros.protein != null ? `${macros.protein} g/kg` : "—"} />
        <DRow k="Carbohydrate" v={macros.carb != null ? `${macros.carb} g/kg` : "—"} />
        <DRow k="Fat" v={macros.fat != null ? `${macros.fat} g/kg` : "—"} />
      </section>

      <section>
        <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-2">Training Week</h3>
        <div className="space-y-1">
          {days.map((d, i) => {
            const ss = d.sessions || [];
            return (
              <div key={i} className="flex gap-3 border-b border-white/5 py-1.5">
                <span className="text-zinc-400 text-xs uppercase tracking-wider w-10 shrink-0 pt-0.5">{DAY_ABBR[i]}</span>
                <div className="text-sm text-white">
                  {ss.length === 0 ? <span className="text-zinc-300">Rest</span> :
                    ss.map((s, j) => (
                      <span key={j} className="block">
                        {labelOf(SPORTS, s.sport)} · {s.duration}min · {labelOf(INTENSITY, s.intensity)} · {s.timing}
                        {s.key ? " · ★Key" : ""}{s.heat ? " · Heat" : ""}
                      </span>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {race.enabled && (
        <section>
          <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Race Week</h3>
          <DRow k="Race day" v={DAY_ABBR[race.dayIndex] || "—"} />
          <DRow k="Event" v={race.event || "—"} />
          <DRow k="Carbs / hr" v={race.carbsPerHour ? `${race.carbsPerHour} g` : "—"} />
          <DRow k="Strategy" v={race.strategy || "—"} />
        </section>
      )}

      {t.bmr && (
        <section>
          <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#34C759] mb-2">Calculated Targets</h3>
          <DRow k="BMR" v={`${t.bmr} kcal`} warn={t.bmr < 1000} />
          <DRow k="Baseline TDEE" v={`${t.baselineTdee} kcal`} />
          <DRow k="Rest-day energy" v={`${t.restEnergy} kcal`} warn={lowEnergy} />
          <DRow k="Weekly demand" v={t.weeklyDemand || "—"} />
          <DRow k="Avg energy / day" v={(t.weekAverage && t.weekAverage.energy) ? `${t.weekAverage.energy} kcal` : "—"} />
        </section>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { logout, email } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityUser, setActivityUser] = useState(null);
  const [activityPlans, setActivityPlans] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackItems, setFeedbackItems] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [tpp, setTpp] = useState([]);
  const [tppBusy, setTppBusy] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPlan, setDetailPlan] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // "Create user" dialog — lets the admin add a member (with optional comp
  // days) without them going through registration + Stripe.
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", comp_days: 0 });
  const [createBusy, setCreateBusy] = useState(false);
  // Edit-user dialog (fixes typos on name/email post-signup).
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [editBusy, setEditBusy] = useState(false);
  // Mark-paid dialog (records offline payment — bank transfer, cash, etc.)
  const [payOpen, setPayOpen] = useState(false);
  const [payUser, setPayUser] = useState(null);
  const [payForm, setPayForm] = useState({ plan: "spc_monthly", days: 0, add_tracker: false, note: "" });
  const [payBusy, setPayBusy] = useState(false);

  // Admin TP calendar setter — paste the athlete's ICS URL on their behalf.
  const [tpOpen, setTpOpen] = useState(false);
  const [tpUser, setTpUser] = useState(null);
  const [tpUrl, setTpUrl] = useState("");
  const [tpBusy, setTpBusy] = useState(false);
  // Admin password reset — force-sets a new password for an athlete.
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data); setUsers(u.data);
    } catch (e) { toast.error(apiError(e, "Could not load admin data")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const grant = (id) => act(() => api.post(`/admin/users/${id}/grant`), "Access granted");
  const revoke = (id) => act(() => api.post(`/admin/users/${id}/revoke`), "Access revoked");
  const reset = (id) => act(() => api.post(`/admin/users/${id}/reset-override`), "Override cleared");
  const comp = (id) => {
    const d = window.prompt("Comp how many free days?", "30");
    const days = parseInt(d, 10);
    if (!days || days < 1) return;
    act(() => api.post(`/admin/users/${id}/comp`, { days }), `Comped ${days} days`);
  };
  const cancelSub = (u) => {
    if (!window.confirm(`Cancel ${u.email}'s subscription now? They lose access immediately and won't be billed again.`)) return;
    act(() => api.post(`/admin/users/${u.id}/cancel-subscription`), "Subscription cancelled");
  };
  const openTp = (u) => { setTpUser(u); setTpUrl(""); setTpOpen(true); };
  const saveTp = async () => {
    if (!tpUrl.trim()) { toast.error("Paste the TrainingPeaks ICS URL"); return; }
    setTpBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${tpUser.id}/set-tp-url`, { url: tpUrl.trim() });
      toast.success(data.message || "TrainingPeaks calendar connected");
      setTpOpen(false); setTpUser(null); setTpUrl(""); load();
    } catch (e) { toast.error(apiError(e, "Could not connect that calendar")); }
    finally { setTpBusy(false); }
  };
  const openPw = (u) => { setPwUser(u); setPwValue(""); setPwOpen(true); };
  const savePw = async () => {
    if (pwValue.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${pwUser.id}/reset-password`, { password: pwValue });
      toast.success(data.message || "Password reset");
      setPwOpen(false); setPwUser(null); setPwValue("");
    } catch (e) { toast.error(apiError(e, "Could not reset password")); }
    finally { setPwBusy(false); }
  };
  const deleteUser = (u) => {
    if (!window.confirm(`Permanently DELETE ${u.email} and ALL their data (plans, logs, journal)? This cannot be undone.`)) return;
    act(() => api.delete(`/admin/users/${u.id}`), "Account deleted");
  };
  // Super-admin "View as athlete" — swap our token for a fresh one scoped
  // to the target user, stash our admin token under `spc_admin_token` so
  // the Header banner can restore it, and land on Home to see exactly what
  // the athlete sees. Admin cannot impersonate another admin.
  const impersonate = async (u) => {
    if (!window.confirm(`View the app as ${u.email}? You'll see their plans, tracker and settings exactly as they do. Exit any time from the yellow banner at the top of the app.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      const myToken = localStorage.getItem("spc_token");
      if (myToken) localStorage.setItem("spc_admin_token", myToken);
      localStorage.setItem("spc_impersonating", JSON.stringify({ email: u.email, name: u.name || "" }));
      localStorage.setItem("spc_token", data.token);
      window.location.href = "/";
    } catch (e) { toast.error(apiError(e, "Could not switch to that account")); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", email: u.email || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editUser) return;
    const name = editForm.name.trim();
    const email = editForm.email.trim().toLowerCase();
    if (!name || !email) { toast.error("Name and email required"); return; }
    setEditBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${editUser.id}`, { name, email });
      toast.success(data?.message || "User updated");
      setEditOpen(false);
      setEditUser(null);
      load();
    } catch (e) { toast.error(apiError(e, "Could not update user")); }
    finally { setEditBusy(false); }
  };

  const openPay = (u) => {
    setPayUser(u);
    setPayForm({ plan: u.subscription?.plan || "spc_monthly", days: 0, add_tracker: false, note: "" });
    setPayOpen(true);
  };
  const savePay = async () => {
    if (!payUser) return;
    setPayBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${payUser.id}/mark-paid`, {
        plan: payForm.plan,
        days: parseInt(payForm.days, 10) || 0,
        add_tracker: !!payForm.add_tracker,
        note: payForm.note || null,
      });
      toast.success(data?.message || "Marked as paid");
      setPayOpen(false);
      setPayUser(null);
      load();
    } catch (e) { toast.error(apiError(e, "Could not mark as paid")); }
    finally { setPayBusy(false); }
  };

  const sendInvite = (u) => {
    if (!window.confirm(`Email ${u.email} a "come subscribe" invite?`)) return;
    act(() => api.post(`/admin/users/${u.id}/send-subscribe-invite`), `Invite sent to ${u.email}`);
  };

  const resetUserData = (u) => {
    const phrase = window.prompt(
      `Wipe ALL data for ${u.email} but keep the login (for reassigning a beta seat)?\n\n` +
      `Deletes: plans, food/water/weight logs, journal, check-ins, photos, recipes, feedback.\n` +
      `Keeps: email + password so the new user can sign in with the same credentials.\n\n` +
      `Type RESET to confirm.`
    );
    if ((phrase || "").toUpperCase().trim() !== "RESET") {
      if (phrase !== null) toast.error("Reset cancelled — confirmation phrase did not match.");
      return;
    }
    act(() => api.post(`/admin/users/${u.id}/reset-data`, { confirm: "RESET" }),
        `Reset — ${u.email} is a fresh account.`);
  };

  const createUser = async () => {
    const name = createForm.name.trim();
    const email = createForm.email.trim().toLowerCase();
    const pw = createForm.password;
    if (!name || !email || pw.length < 8) { toast.error("Name, email, and 8+ char password required"); return; }
    setCreateBusy(true);
    try {
      await api.post("/admin/users", { name, email, password: pw, comp_days: parseInt(createForm.comp_days, 10) || 0 });
      toast.success(`Created ${email}${createForm.comp_days > 0 ? ` — comped ${createForm.comp_days} days` : ""}`);
      setCreateOpen(false);
      setCreateForm({ name: "", email: "", password: "", comp_days: 0 });
      load();
    } catch (e) { toast.error(apiError(e, "Could not create user")); }
    finally { setCreateBusy(false); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  const rel = (d) => {
    if (!d) return "—";
    const then = new Date(d).getTime();
    const secs = Math.round((Date.now() - then) / 1000);
    if (secs < 60) return "just now";
    const mins = Math.round(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(d).toLocaleDateString();
  };

  const viewActivity = async (u) => {
    setActivityUser(u);
    setActivityPlans(null);
    setActivityLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/plans`);
      setActivityPlans(data.plans || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load this tester's plans"));
      setActivityPlans([]);
    } finally { setActivityLoading(false); }
  };
  const closeActivity = () => { setActivityUser(null); setActivityPlans(null); };

  const viewPlanDetails = async (planId) => {
    setDetailOpen(true); setDetailPlan(null); setDetailLoading(true);
    try { const { data } = await api.get(`/admin/plans/${planId}`); setDetailPlan(data); }
    catch (e) { toast.error(apiError(e, "Could not load plan details")); setDetailOpen(false); }
    finally { setDetailLoading(false); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm("Delete this plan permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/plans/${planId}`);
      setActivityPlans((ps) => (ps || []).filter((p) => p.id !== planId));
      setUsers((us) => us.map((u) => (u.id === activityUser?.id ? { ...u, planCount: Math.max(0, (u.planCount || 1) - 1) } : u)));
      toast.success("Plan deleted");
    } catch (e) {
      toast.error(apiError(e, "Could not delete plan"));
    }
  };
  const STEP_LABELS = ["Profile", "Dietary", "Targets", "Training", "Plan", "Pathways", "Shopping", "Summary"];

  const openFeedback = async () => {
    setFeedbackOpen(true); setFeedbackItems(null); setFeedbackLoading(true);
    try { const { data } = await api.get("/admin/feedback"); setFeedbackItems(data || []); }
    catch (e) { toast.error(apiError(e, "Could not load feedback")); setFeedbackItems([]); }
    finally { setFeedbackLoading(false); }
  };
  const deleteFeedback = async (fid) => {
    if (!window.confirm("Delete this feedback?")) return;
    try { await api.delete(`/admin/feedback/${fid}`); setFeedbackItems((it) => (it || []).filter((f) => f.id !== fid)); toast.success("Deleted"); }
    catch (e) { toast.error(apiError(e, "Could not delete")); }
  };

  const loadTpp = async () => {
    try { const { data } = await api.get("/admin/tp-premium"); setTpp(data.requests || []); }
    catch (e) { console.error("Admin: loadTpp failed", e); }
  };
  useEffect(() => { loadTpp(); }, []);
  const tppAction = async (em, action) => {
    setTppBusy(em);
    try { await api.post(`/admin/tp-premium/${encodeURIComponent(em)}/${action}`); await loadTpp(); toast.success(action === "activate" ? "Marked active" : "Deactivated"); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
    finally { setTppBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <div className="leading-none">
              <p className="font-display text-lg uppercase tracking-tight"><span className="text-[#E2E30A]">Performance</span> Nutrition</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-200 mt-1">Admin · Powered by SPC</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-build-plan-btn" variant="outline" onClick={() => navigate("/app")} className="h-9 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase tracking-wider">
              <FilePlus2 className="h-4 w-4 mr-1.5" /> Build a Plan
            </Button>
            <Button data-testid="admin-feedback-btn" variant="outline" onClick={openFeedback} className="h-9 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase tracking-wider">
              <MessageSquare className="h-4 w-4 mr-1.5" /> Feedback
            </Button>
            <button data-testid="admin-settings-btn" onClick={() => setSettingsOpen(true)} title="Account settings — change your display name"
              className="p-2 border border-white/10 text-zinc-200 hover:text-[#E2E30A] hover:border-[#E2E30A]/50 transition-colors"><Settings className="h-4 w-4" /></button>
            <button data-testid="admin-logout-btn" onClick={logout} className="p-2 border border-white/10 text-zinc-200 hover:text-[#FF3B30] transition-colors"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-dashboard">
        <h1 className="font-display text-4xl uppercase tracking-tighter mb-8">Dashboard</h1>
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-200 py-10"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
              <StatCard icon={Users} label="Members" value={stats.totalMembers} testId="stat-members" />
              <StatCard icon={Crown} label="Active subs" value={stats.activeSubscribers} testId="stat-active" />
              <StatCard icon={Clock} label="On trial" value={stats.trialing} testId="stat-trialing" />
              <StatCard icon={ShieldCheck} label="With access" value={stats.withAccess} testId="stat-access" />
              <StatCard icon={PoundSterling} label={`Revenue (${stats.currency})`} value={stats.revenue.toFixed(2)} testId="stat-revenue" />
            </div>

            <RecipeSubmissions />

            <ReleaseNotesEditor />

            {tpp.length > 0 && (
              <div className="mb-10 border border-[#4C9AFF]/30 p-5" data-testid="admin-tp-premium">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#4C9AFF] mb-3">TrainingPeaks Premium — add these athletes in your TP Coach account</p>
                <div className="space-y-2">
                  {tpp.map((r) => {
                    const t = r.tp_premium || {};
                    const badge = { pending: "text-[#FFB454]", active: "text-[#34C759]", cancelling: "text-[#FF9500]", cancelled: "text-zinc-300" }[t.status] || "text-zinc-400";
                    return (
                      <div key={r.email} data-testid={`tpp-row-${r.email}`} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-[#141414] px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{r.name || r.email} <span className={`ml-1 text-[10px] uppercase ${badge}`}>{t.status}</span></p>
                          <p className="text-[11px] font-mono text-[#4C9AFF]">TP: {t.tpEmail || "—"}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {t.status !== "active" && (
                            <button data-testid={`tpp-activate-${r.email}`} disabled={tppBusy === r.email} onClick={() => tppAction(r.email, "activate")}
                              className="text-[11px] uppercase tracking-wide border border-[#34C759]/50 text-[#34C759] px-2 py-1 hover:bg-[#34C759]/10 disabled:opacity-50">Mark active</button>
                          )}
                          {["pending", "active", "cancelling"].includes(t.status) && (
                            <button data-testid={`tpp-deactivate-${r.email}`} disabled={tppBusy === r.email} onClick={() => tppAction(r.email, "deactivate")}
                              className="text-[11px] uppercase tracking-wide border border-white/20 text-zinc-300 px-2 py-1 hover:bg-white/5 disabled:opacity-50">Remove</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {stats.upcomingRenewals?.length > 0 && (
              <div className="mb-10 border border-white/10 p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-200 mb-3">Renewals in next 30 days</p>
                <div className="flex flex-wrap gap-2">
                  {stats.upcomingRenewals.map((r) => (
                    <span key={r.email} className="text-xs border border-white/10 px-2 py-1 text-zinc-200">{r.name || r.email} · {r.plan} · {fmt(r.renews)}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Members ({users.length})</p>
                <Button data-testid="admin-create-user-btn" onClick={() => setCreateOpen(true)}
                  className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-[11px] uppercase font-semibold h-8 px-3">
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Create user
                </Button>
              </div>
              <table className="w-full text-sm min-w-[960px]" data-testid="admin-users-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-zinc-200 border-b border-white/10">
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Subscription</th>
                    <th className="px-4 py-3">Access</th>
                    <th className="px-4 py-3">Activity</th>
                    <th className="px-4 py-3">Last login</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white">{u.name || "—"} {u.role === "admin" && <span className="text-[#E2E30A] text-[10px] uppercase ml-1">Admin</span>}</p>
                        <p className="text-zinc-200 text-xs break-all">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLOR[u.subscription?.status] || STATUS_COLOR.none}`}>{u.subscription?.status || "none"}</span>
                        {u.subscription?.plan && <p className="text-zinc-200 text-xs mt-1">{u.subscription.plan.replace("spc_", "")}</p>}
                        {u.compUntil && <p className="text-[#4C9AFF] text-xs mt-1">comp → {fmt(u.compUntil)}</p>}
                      </td>
                      <td className="px-4 py-3">
                        {u.hasAccess ? <span className="text-[#34C759] text-xs">● Active</span> : <span className="text-zinc-200 text-xs">○ None</span>}
                        {u.accessOverride && <p className="text-zinc-200 text-[10px] uppercase mt-1">override: {u.accessOverride}</p>}
                      </td>
                      <td className="px-4 py-3">
                        {u.planCount > 0 ? (
                          <button data-testid={`admin-activity-${u.id}`} onClick={() => viewActivity(u)}
                            className="inline-flex items-center gap-1.5 text-xs text-[#4C9AFF] hover:text-white border border-[#4C9AFF]/40 hover:border-[#4C9AFF] px-2 py-1 transition-colors">
                            <FileText className="h-3 w-3" /> {u.planCount} plan{u.planCount === 1 ? "" : "s"}
                          </button>
                        ) : (
                          <span className="text-zinc-200 text-xs">No plans yet</span>
                        )}
                        {u.lastActivity && <p className="text-zinc-200 text-[10px] mt-1">active {fmt(u.lastActivity)}</p>}
                      </td>
                      <td className="px-4 py-3" data-testid={`admin-lastlogin-${u.id}`}>
                        {u.lastActive || u.lastLogin ? (
                          <>
                            <p className="text-white text-xs">{rel(u.lastActive || u.lastLogin)}</p>
                            <p className="text-zinc-200 text-[10px] mt-1">
                              {u.lastActive ? "last seen" : "last login"} · {u.loginCount || 0} login{u.loginCount === 1 ? "" : "s"}
                            </p>
                          </>
                        ) : (
                          <span className="text-zinc-200 text-xs">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-200 text-xs">{fmt(u.createdAt)}</td>
                      <td className="px-4 py-3">
                        {u.role !== "admin" && (
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {/* Coach controls — admin-only. Promote/demote and
                                (once promoted) assign athletes to this coach. */}
                            <select data-testid={`admin-role-${u.id}`} value={u.role || "member"}
                              onChange={(e) => act(() => api.post(`/admin/users/${u.id}/role`, { role: e.target.value }), `Role → ${e.target.value}`)}
                              className="bg-[#0A0A0A] border border-white/15 text-white text-[10px] uppercase font-bold h-7 px-1.5">
                              <option value="member">Member</option>
                              <option value="coach">Coach</option>
                            </select>
                            {u.role !== "coach" && u.role !== "admin" && (
                              <select data-testid={`admin-coach-${u.id}`} value={u.coachEmail || ""}
                                onChange={(e) => act(() => api.post(`/admin/users/${u.id}/coach`, { coach_email: e.target.value || null }), e.target.value ? `Assigned to ${e.target.value}` : "Coach unlinked")}
                                className="bg-[#0A0A0A] border border-white/15 text-white text-[10px] h-7 px-1.5 max-w-[140px]">
                                <option value="">No coach</option>
                                {users.filter((x) => x.role === "coach" || x.role === "admin").map((c) => (
                                  <option key={c.id} value={c.email}>{c.name || c.email.split("@")[0]}</option>
                                ))}
                              </select>
                            )}
                            <button data-testid={`admin-edit-${u.id}`} onClick={() => openEdit(u)} title="Edit name / email" className="p-1.5 border border-white/10 text-white hover:bg-white/10"><PencilLine className="h-3.5 w-3.5" /></button>
                            {u.role !== "admin" && (
                              <button data-testid={`admin-impersonate-${u.id}`} onClick={() => impersonate(u)} title="View app as this athlete" className="p-1.5 border border-[#E2E30A]/40 text-[#E2E30A] hover:bg-[#E2E30A]/10"><UserCheck className="h-3.5 w-3.5" /></button>
                            )}
                            <button data-testid={`admin-grant-${u.id}`} onClick={() => grant(u.id)} title="Grant access" className="p-1.5 border border-white/10 text-[#34C759] hover:bg-[#34C759]/10"><ShieldCheck className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-revoke-${u.id}`} onClick={() => revoke(u.id)} title="Revoke access" className="p-1.5 border border-white/10 text-[#FF3B30] hover:bg-[#FF3B30]/10"><ShieldOff className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-comp-${u.id}`} onClick={() => comp(u.id)} title="Comp free days" className="p-1.5 border border-white/10 text-[#E2E30A] hover:bg-[#E2E30A]/10"><Gift className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-mark-paid-${u.id}`} onClick={() => openPay(u)} title="Mark as paid (offline payment)" className="p-1.5 border border-white/10 text-[#34C759] hover:bg-[#34C759]/10"><BadgePoundSterling className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-send-invite-${u.id}`} onClick={() => sendInvite(u)} title="Email a subscribe invite" className="p-1.5 border border-white/10 text-[#4C9AFF] hover:bg-[#4C9AFF]/10"><MailPlus className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-reset-${u.id}`} onClick={() => reset(u.id)} title="Clear override" className="p-1.5 border border-white/10 text-zinc-200 hover:bg-white/10"><RotateCcw className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-set-tp-${u.id}`} onClick={() => openTp(u)} title="Set TrainingPeaks calendar URL" className="p-1.5 border border-white/10 text-[#4C9AFF] hover:bg-[#4C9AFF]/10"><CalendarDays className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-reset-pw-${u.id}`} onClick={() => openPw(u)} title="Reset password" className="p-1.5 border border-white/10 text-[#FFB454] hover:bg-[#FFB454]/10"><KeyRound className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-cancel-sub-${u.id}`} onClick={() => cancelSub(u)} title="Cancel subscription" className="p-1.5 border border-white/10 text-[#FF9500] hover:bg-[#FF9500]/10"><Ban className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-reset-data-${u.id}`} onClick={() => resetUserData(u)} title="Wipe all data (keep the login)" className="p-1.5 border border-[#FF9500]/40 text-[#FF9500] hover:bg-[#FF9500]/10"><Eraser className="h-3.5 w-3.5" /></button>
                            <button data-testid={`admin-delete-user-${u.id}`} onClick={() => deleteUser(u)} title="Delete account permanently" className="p-1.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {/* Admin-only "Create user" dialog — creates a member directly (no
          Stripe), optionally comping them so they land with instant access. */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="admin-create-user-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Create user</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">Adds a member account directly. Skip Stripe by comping days — leave at 0 to make them subscribe normally.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Full name</label>
              <Input data-testid="create-user-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Jane Doe" className="bg-black border-white/15 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Email</label>
              <Input data-testid="create-user-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="jane@example.com" className="bg-black border-white/15 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Temporary password (min 8 chars)</label>
              <Input data-testid="create-user-password" type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Send this to the athlete" className="bg-black border-white/15 text-white mt-1 font-mono" />
            <p className="text-[10px] text-zinc-300 mt-1">Share this with the athlete — we'll email them these details automatically so they can sign in and change the password.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Comp free days (0 = no comp)</label>
              <Input data-testid="create-user-comp-days" type="number" min="0" max="3650" value={createForm.comp_days} onChange={(e) => setCreateForm({ ...createForm, comp_days: e.target.value })}
                className="bg-black border-white/15 text-white mt-1" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="create-user-cancel" onClick={() => setCreateOpen(false)} variant="outline"
                className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">Cancel</Button>
              <Button data-testid="create-user-submit" onClick={createUser} disabled={createBusy}
                className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
                {createBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create user"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog — fixes typos on name/email post-signup.
          Email edits migrate the identifier across every user-scoped collection. */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="admin-edit-user-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Edit user</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Fix a typo or update the athlete's contact details. Changing the email
              migrates their plans, logs and journal — no data lost.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Full name</label>
              <Input data-testid="edit-user-name" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-black border-white/15 text-white mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Email</label>
              <Input data-testid="edit-user-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-black border-white/15 text-white mt-1" />
              {editUser && editForm.email.trim().toLowerCase() !== (editUser.email || "").toLowerCase() && (
                <p className="text-[10px] text-[#FF9500] mt-1">
                  Email change: their plans, journal, food/water/weight logs and photos will
                  all be re-linked to the new address.
                </p>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="edit-user-cancel" onClick={() => setEditOpen(false)} variant="outline"
                className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">Cancel</Button>
              <Button data-testid="edit-user-save" onClick={saveEdit} disabled={editBusy}
                className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
                {editBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tpOpen} onOpenChange={setTpOpen}>
        <DialogContent data-testid="admin-set-tp-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Set TrainingPeaks calendar</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Paste the athlete's TrainingPeaks ICS calendar URL. We'll validate it by pulling
              their sessions before saving. {tpUser ? `For ${tpUser.email}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">ICS URL (webcal:// or https://tpapi.trainingpeaks.com/...)</label>
            <Input data-testid="admin-set-tp-url" value={tpUrl} onChange={(e) => setTpUrl(e.target.value)}
              placeholder="webcal://tpapi.trainingpeaks.com/…/ical" className="bg-black border-white/15 text-white font-mono text-xs" />
            <p className="text-[10px] text-zinc-500">Athletes can copy this from TrainingPeaks → Settings → Calendar → Export URL.</p>
            <div className="flex gap-2 pt-2">
              <Button data-testid="admin-set-tp-cancel" onClick={() => setTpOpen(false)} variant="outline"
                className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">Cancel</Button>
              <Button data-testid="admin-set-tp-save" onClick={saveTp} disabled={tpBusy || !tpUrl.trim()}
                className="flex-1 rounded-none bg-[#4C9AFF] hover:bg-[#3d8ae6] text-black text-xs uppercase font-semibold">
                {tpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect calendar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="admin-reset-pw-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Reset password</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Force-sets a new password. Existing sessions on their devices are signed out.
              Share the new password with them securely. {pwUser ? `For ${pwUser.email}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">New password (min 8 chars)</label>
            <Input data-testid="admin-reset-pw-input" type="text" value={pwValue}
              onChange={(e) => setPwValue(e.target.value)} placeholder="TempPass2026!"
              className="bg-black border-white/15 text-white font-mono" />
            <div className="flex gap-2 pt-2">
              <Button data-testid="admin-reset-pw-cancel" onClick={() => setPwOpen(false)} variant="outline"
                className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">Cancel</Button>
              <Button data-testid="admin-reset-pw-save" onClick={savePw} disabled={pwBusy || pwValue.length < 8}
                className="flex-1 rounded-none bg-[#FFB454] hover:bg-[#f0a03e] text-black text-xs uppercase font-semibold">
                {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark-paid dialog — records an offline / bank-transfer payment.
          Sets subscription.status=active with provider="offline". No Stripe involved. */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="admin-mark-paid-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Mark as paid</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Records an offline payment (bank transfer, cash, invoice). No Stripe charge
              is created — this just marks the account active for the period you set.
              {payUser ? ` For ${payUser.email}.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Plan</label>
              <select data-testid="mark-paid-plan" value={payForm.plan}
                onChange={(e) => setPayForm({ ...payForm, plan: e.target.value })}
                className="w-full bg-black border border-white/15 text-white mt-1 px-3 py-2 text-sm">
                <option value="spc_monthly">Full · Monthly (£14.99)</option>
                <option value="spc_lite_monthly">Lite · Monthly (£9.99)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">
                Duration (days) — leave 0 for 30 days
              </label>
              <Input data-testid="mark-paid-days" type="number" min="0" max="3650" value={payForm.days}
                onChange={(e) => setPayForm({ ...payForm, days: e.target.value })}
                className="bg-black border-white/15 text-white mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
              <input type="checkbox" data-testid="mark-paid-tracker" checked={payForm.add_tracker}
                onChange={(e) => setPayForm({ ...payForm, add_tracker: e.target.checked })}
                className="accent-[#E2E30A] h-4 w-4" />
              Include Food Tracker add-on
            </label>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400">Note (optional — kept on the record)</label>
              <Input data-testid="mark-paid-note" value={payForm.note}
                onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                placeholder="e.g. Bank transfer ref #A123, paid £149" className="bg-black border-white/15 text-white mt-1" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="mark-paid-cancel" onClick={() => setPayOpen(false)} variant="outline"
                className="flex-1 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">Cancel</Button>
              <Button data-testid="mark-paid-submit" onClick={savePay} disabled={payBusy}
                className="flex-1 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black text-xs uppercase font-semibold">
                {payBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark as paid"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={!!activityUser} onOpenChange={(o) => !o && closeActivity()}>
        <DialogContent data-testid="admin-activity-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">
              {activityUser?.name || activityUser?.email}
            </DialogTitle>
            <DialogDescription className="text-zinc-200 text-xs break-all">{activityUser?.email} · tester activity</DialogDescription>
          </DialogHeader>
          {activityLoading ? (
            <div className="flex items-center gap-2 text-zinc-200 py-8"><Loader2 className="h-5 w-5 animate-spin" /> Loading plans…</div>
          ) : !activityPlans || activityPlans.length === 0 ? (
            <p className="text-zinc-200 text-sm py-8">This tester hasn't built any plans yet.</p>
          ) : (
            <div className="space-y-3 py-2">
              {activityPlans.map((p) => (
                <div key={p.id} data-testid={`admin-activity-plan-${p.id}`} className="border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display uppercase text-[#E2E30A] truncate">{p.name || "Untitled plan"}</p>
                      <p className="text-xs text-zinc-200 mt-1">
                        {p.athleteName ? `${p.athleteName} · ` : ""}Goal: {p.goal || "—"}
                        {p.weekStart ? ` · Week starting ${p.weekStart}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase tracking-wider border border-white/15 px-2 py-1 text-zinc-200">
                        Step {p.currentStep || 1}/8 · {STEP_LABELS[(p.currentStep || 1) - 1]}
                      </span>
                      <button data-testid={`admin-view-plan-${p.id}`} onClick={() => viewPlanDetails(p.id)} title="View full plan details"
                        className="p-1.5 border border-[#4C9AFF]/40 text-[#4C9AFF] hover:bg-[#4C9AFF]/10 transition-colors">
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`admin-delete-plan-${p.id}`} onClick={() => deletePlan(p.id)} title="Delete plan permanently"
                        className="p-1.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-200 mt-3 pt-3 border-t border-white/10">
                    <span className={p.generated ? "text-[#34C759]" : "text-zinc-200"}>
                      {p.generated ? "● Plan generated" : "○ Not generated yet"}
                    </span>
                    {p.generated && p.avgEnergy != null && <span>Avg {p.avgEnergy} kcal/day</span>}
                    {p.generated && p.weeklyDemand && <span>Weekly load: {p.weeklyDemand}</span>}
                    <span className="ml-auto">Updated {fmt(p.updatedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent data-testid="admin-feedback-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">Beta Feedback</DialogTitle>
            <DialogDescription className="text-zinc-200 text-xs">Bug reports and ideas from testers.</DialogDescription>
          </DialogHeader>
          {feedbackLoading ? (
            <div className="flex items-center gap-2 text-zinc-200 py-8"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
          ) : !feedbackItems || feedbackItems.length === 0 ? (
            <p className="text-zinc-200 text-sm py-8">No feedback yet.</p>
          ) : (
            <div className="space-y-3 py-2">
              {feedbackItems.map((f) => {
                const color = f.type === "bug" ? "#FF3B30" : f.type === "suggestion" ? "#E2E30A" : "#4C9AFF";
                return (
                  <div key={f.id} data-testid={`admin-feedback-${f.id}`} className="border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-[10px] uppercase tracking-wider border px-2 py-1" style={{ borderColor: `${color}66`, color }}>{f.type}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[11px] text-zinc-200">{fmt(f.createdAt)}</span>
                        <button data-testid={`admin-delete-feedback-${f.id}`} onClick={() => deleteFeedback(f.id)} title="Delete feedback"
                          className="p-1.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <p className="text-sm text-white mt-3 whitespace-pre-wrap">{f.message}</p>
                    <p className="text-[11px] text-zinc-200 mt-2">{f.name || f.email} · {f.email}</p>
                    {f.page && <p className="text-[10px] text-zinc-200 mt-1 break-all">{f.page}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="admin-plan-detail-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-xl">{detailPlan?.name || "Plan details"}</DialogTitle>
            <DialogDescription className="text-zinc-200 text-xs break-all">
              {detailPlan?.profile?.name ? `${detailPlan.profile.name} · ` : ""}{detailPlan?.email || ""}
            </DialogDescription>
          </DialogHeader>
          {detailLoading || !detailPlan ? (
            <div className="flex items-center gap-2 text-zinc-200 py-8"><Loader2 className="h-5 w-5 animate-spin" /> Loading plan…</div>
          ) : (
            <PlanDetail p={detailPlan} />
          )}
        </DialogContent>
      </Dialog>
      <AccountSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
